import { useState, useEffect } from "react"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"
import toast from "react-hot-toast"
import {Plus, Trash2} from 'lucide-react'
import type { SpecItem, SpecPart } from "../hooks/useSpecs"

// 리스트/표 등 서식 지원 (요구사양·가이드라인을 깔끔하게 정리)
const quillModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }],
    ["clean"],
  ],
}

interface SubmitData {
  category: string
  subCategory: string
  requirement: string
  guideline: string
  remark: string
  parts: SpecPart[]
  changeReason: string
  requestedByName: string
}

export function SpecItemEditModal({
  mode,
  item,
  defaultCategory,
  categoryOnly,
  defaultRequestedBy,
  onClose,
  onSubmit,
}: {
  mode: "create" | "update"
  item?: SpecItem
  defaultCategory?: string
  categoryOnly?: boolean
  defaultRequestedBy: string
  onClose: () => void
  onSubmit: (data: SubmitData) => void
}) {
  const [category, setCategory] = useState(item?.category || defaultCategory || "")
  const [subCategory, setSubCategory] = useState(item?.subCategory || "")
  const [requirement, setRequirement] = useState(item?.requirement || "")
  const [guideline, setGuideline] = useState(item?.guideline || "")
  const [remark, setRemark] = useState(item?.remark || "")
  const [changeReason, setChangeReason] = useState("")
  const [requestedByName, setRequestedByName] = useState(defaultRequestedBy)
  const [parts, setParts] = useState<SpecPart[]>(
    (item?.parts && item.parts.length > 0 ? item.parts : []) as SpecPart[]
  )
  const [submitting, setSubmitting] = useState(false)
  // 요구사양/가이드라인 에디터 높이 (기본 2줄 = 약 60px). 드래그로 자유 조절
  const [reqHeight, setReqHeight] = useState(60)
  const [guideHeight, setGuideHeight] = useState(60)

  // 드래그 리사이즈: 시작 위치와 높이를 기준으로 마우스 이동량만큼 증감
  const startResize = (
    e: React.MouseEvent,
    current: number,
    setter: (v: number) => void
  ) => {
    e.preventDefault()
    const startY = e.clientY
    const startH = current
    const onMove = (ev: MouseEvent) => {
      const next = Math.min(600, Math.max(48, startH + (ev.clientY - startY)))
      setter(next)
    }
    const onUp = () => {
      document.removeEventListener("mousemove", onMove)
      document.removeEventListener("mouseup", onUp)
    }
    document.addEventListener("mousemove", onMove)
    document.addEventListener("mouseup", onUp)
  }

  const addPart = () =>
    setParts((prev) => [...prev, { partName: "", partNumber: "", quantity: 1, maker: "", remark: "" }])
  const updatePart = (idx: number, key: keyof SpecPart, value: string | number) =>
    setParts((prev) => prev.map((p, i) => (i === idx ? { ...p, [key]: value } : p)))
  const removePart = (idx: number) => setParts((prev) => prev.filter((_, i) => i !== idx))

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleOverlayClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, subCategory, requirement, guideline, remark, changeReason, parts, onClose])

  const isEmptyHtml = (html: string) => !html || html.replace(/<[^>]*>/g, "").trim() === ""

  // 입력 내용이 하나라도 있으면 true (외부 클릭 시 폐기 확인용)
  const hasContent = () =>
    !!category.trim() ||
    !!subCategory.trim() ||
    !isEmptyHtml(requirement) ||
    !isEmptyHtml(guideline) ||
    !!remark.trim() ||
    !!changeReason.trim() ||
    parts.some((p) => (p.partName || "").trim() || (p.partNumber || "").trim())

  // 오버레이(외부) 클릭 시 닫기 처리
  // - 수정 모드(update): 이미 등록된 항목을 여는 경우이므로 "수정 사항 없이 닫습니까?"로 확인
  //   -> 예: 아무 action 없이 팝업 닫기 / 아니오: 확인창만 닫고 팝업은 그대로 유지
  // - 추가 모드(create): 새로 입력한 내용이 있으면 "입력 내용을 폐기하겠습니까?"로 확인
  const handleOverlayClose = () => {
    if (mode === "update") {
      if (!window.confirm("수정 사항 없이 닫습니까?")) {
        return
      }
      onClose()
      return
    }
    if (hasContent() && !window.confirm("입력한 내용이 있습니다. 입력 내용을 폐기하겠습니까?")) {
      return
    }
    onClose()
  }

  // 대분류 항목 추가 전용: 대분류만 입력받고 나머지는 빈 값으로 제출
  const handleCategoryOnlySubmit = () => {
    if (submitting) return
    if (!category.trim()) {
      toast.error("대분류를 입력하세요.")
      return
    }
    if (!requestedByName.trim()) {
      toast.error("수정 요청자를 입력하세요.")
      return
    }
    setSubmitting(true)
    onSubmit({
      category: category.trim(),
      subCategory: "",
      requirement: "",
      guideline: "",
      remark: "",
      parts: [],
      changeReason: changeReason.trim(),
      requestedByName: requestedByName.trim(),
    })
  }

  const handleSubmit = () => {
    if (submitting) return
    if (!category.trim()) {
      toast.error("대분류를 입력하세요.")
      return
    }
    if (!subCategory.trim()) {
      toast.error("중분류를 입력하세요.")
      return
    }
    if (isEmptyHtml(requirement)) {
      toast.error("요구 사양을 입력하세요.")
      return
    }
    if (!requestedByName.trim()) {
      toast.error("수정 요청자를 입력하세요.")
      return
    }
    // 형번이 비어 있는 부품 행은 저장 대상에서 제외 (수량은 최소 1)
    const cleanParts: SpecPart[] = parts
      .filter((p) => (p.partNumber || "").trim() !== "" || (p.partName || "").trim() !== "")
      .map((p) => ({
        partName: (p.partName || "").trim(),
        partNumber: (p.partNumber || "").trim(),
        quantity: Number(p.quantity) > 0 ? Number(p.quantity) : 1,
        maker: (p.maker || "").trim(),
        remark: (p.remark || "").trim(),
      }))

    setSubmitting(true)
    onSubmit({
      category: category.trim(),
      subCategory: subCategory.trim(),
      requirement,
      guideline,
      remark: remark.trim(),
      parts: cleanParts,
      changeReason: changeReason.trim(),
      requestedByName: requestedByName.trim(),
    })
  }

  // 대분류만 추가하는 간소화 모달
  if (categoryOnly) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={handleOverlayClose}>
        <div
          className="bg-white rounded-2xl w-full max-w-md my-8 p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-bold text-gray-900">대분류 항목 추가</h2>
          </div>
          <p className="text-sm text-amber-600 mb-4">
            새 <b>대분류</b>를 추가합니다. 저장하면 <b>미승인</b> 상태로 등록되며, 팀장급 승인 후 반영됩니다.
          </p>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">대분류 *</label>
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="예: 전동 축"
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">수정 요청자 *</label>
              <input
                value={requestedByName}
                onChange={(e) => setRequestedByName(e.target.value)}
                placeholder="수정을 요청한 사람"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">변경 사유</label>
              <input
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder="예: 신규 대분류 추가"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">
              취소
            </button>
            <button
              onClick={handleCategoryOnlySubmit}
              disabled={submitting}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "등록 중..." : "추가하기"}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={handleOverlayClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-2xl my-8 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`.quill-fill{display:flex;flex-direction:column}.quill-fill>div{flex:1 1 auto;display:flex;flex-direction:column;min-height:0}.quill-fill .ql-container{flex:1 1 auto;overflow-y:auto;min-height:0}`}</style>
        <div className="flex items-center justify-between gap-3 mb-1">
          <h2 className="text-lg font-bold text-gray-900">
            {mode === "create" ? "사양 항목 추가" : "사양 항목 수정 요청"}
          </h2>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="shrink-0 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "등록 중..." : "수정 요청 등록"}
          </button>
        </div>
        <p className="text-sm text-amber-600 mb-4">
          저장하면 <b>미승인</b> 상태로 등록되며, 팀장급 승인 후 실제 사양서에 반영됩니다.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">대분류 *</label>
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="예: 전동 축"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">중분류 *</label>
            <input
              value={subCategory}
              onChange={(e) => setSubCategory(e.target.value)}
              placeholder="예: 토치 좌우 위빙"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">요구 사양 * (원청 요구)</label>
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <div style={{ height: reqHeight + 42 }} className="quill-fill">
              <ReactQuill theme="snow" value={requirement} onChange={setRequirement} modules={quillModules} />
            </div>
            <div
              onMouseDown={(e) => startResize(e, reqHeight, setReqHeight)}
              className="h-2.5 bg-gray-100 hover:bg-orange-200 cursor-ns-resize flex items-center justify-center border-t border-gray-200"
              title="드래그해서 높이 조절"
            >
              <div className="w-8 h-1 rounded-full bg-gray-400" />
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">제작 가이드라인 (사내 제작 방법)</label>
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <div style={{ height: guideHeight + 42 }} className="quill-fill">
              <ReactQuill theme="snow" value={guideline} onChange={setGuideline} modules={quillModules} />
            </div>
            <div
              onMouseDown={(e) => startResize(e, guideHeight, setGuideHeight)}
              className="h-2.5 bg-gray-100 hover:bg-orange-200 cursor-ns-resize flex items-center justify-center border-t border-gray-200"
              title="드래그해서 높이 조절"
            >
              <div className="w-8 h-1 rounded-full bg-gray-400" />
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">비고</label>
          <textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            rows={2}
            placeholder="예: 고객 특이사항, 참고 메모 등"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-y"
          />
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">사용 부품 (형번 / 수량)</label>
            <button
              type="button"
              onClick={addPart}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg font-medium"
            >
              <Plus className="w-4 h-4" /> 부품 추가
            </button>
          </div>

          {parts.length === 0 ? (
            <p className="text-sm text-gray-400 border border-dashed border-gray-300 rounded-lg py-4 text-center">
              등록된 부품이 없습니다. "부품 추가"로 형번과 수량을 입력하세요.
            </p>
          ) : (
            <div className="space-y-2">
              <div className="hidden sm:grid grid-cols-12 gap-2 px-1 text-xs font-medium text-gray-500">
                <div className="col-span-3">부품명</div>
                <div className="col-span-3">형번(품번)</div>
                <div className="col-span-1 text-center">수량</div>
                <div className="col-span-2">제조사</div>
                <div className="col-span-2">비고</div>
                <div className="col-span-1"></div>
              </div>
              {parts.map((p, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    value={p.partName || ""}
                    onChange={(e) => updatePart(idx, "partName", e.target.value)}
                    placeholder="부품명"
                    className="col-span-6 sm:col-span-3 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                  <input
                    value={p.partNumber || ""}
                    onChange={(e) => updatePart(idx, "partNumber", e.target.value)}
                    placeholder="형번"
                    className="col-span-6 sm:col-span-3 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                  <input
                    type="number"
                    min={1}
                    value={p.quantity ?? 1}
                    onChange={(e) => updatePart(idx, "quantity", Number(e.target.value))}
                    placeholder="수량"
                    className="col-span-4 sm:col-span-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none text-center"
                  />
                  <input
                    value={p.maker || ""}
                    onChange={(e) => updatePart(idx, "maker", e.target.value)}
                    placeholder="제조사"
                    className="col-span-4 sm:col-span-2 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                  <input
                    value={p.remark || ""}
                    onChange={(e) => updatePart(idx, "remark", e.target.value)}
                    placeholder="비고"
                    className="col-span-3 sm:col-span-2 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => removePart(idx)}
                    className="col-span-1 flex items-center justify-center text-red-500 hover:text-red-700"
                    title="삭제"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">수정 요청자 *</label>
            <input
              value={requestedByName}
              onChange={(e) => setRequestedByName(e.target.value)}
              placeholder="수정을 요청한 사람"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">변경 사유</label>
            <input
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="예: 고객 요구 변경"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "등록 중..." : "수정 요청 등록"}
          </button>
        </div>
      </div>
    </div>
  )
}
